
import { makeStyles } from 'tss-react/mui';
import { UserQuery_node_TEDSYSUser__Associations_Franvaro_All } from '../../Queries/_QueryTypes/UserQuery';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import EDListItem from '../../../../../../../BaseComponents/List/Components/EDListItem';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDTooltip from '../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDCaptionedValue from '../../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import { useIsBreakpointOrSmaller } from '../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';

interface IProps {
  isAuto: boolean;
  onDeleteClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onEditClick?: (event: React.MouseEvent<HTMLElement>) => void;
  level: number;
  pointer: boolean;
  franvaro: UserQuery_node_TEDSYSUser__Associations_Franvaro_All;
  isReservFor?: boolean;
  index: number;
}

const useStyles = makeStyles<IProps>()((theme, props) => ({
  container: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexFlow: 'row nowrap',
    width: '100%',
    paddingLeft: `${8 * props.level + 8}px`,
    fontStyle: `${props.isAuto ? 'italic' : 'normal'}`,
    borderLeft: props.isAuto ? '4px solid lightgray' : '',

    ['&:last-child']: { borderBottom: 'none' },
  },
  item: {
    width: 'calc(100% / 4 - 48px / 4)',
    ['&:last-child']: { width: '48px' },
  },

  containerSmall: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingLeft: `${8 * props.level + 8}px`,
    fontStyle: `${props.isAuto ? 'italic' : 'normal'}`,
    borderLeft: props.isAuto ? '4px solid lightgray' : '',

    ['&:last-child']: { borderBottom: 'none' },
  },

  itemSmall: {
    width: '100%',
    ['&:last-child']: { width: '48px' },
  },

  pointer: { cursor: 'pointer' },
}));

const getIsForText = (
  franvaro: UserQuery_node_TEDSYSUser__Associations_Franvaro_All
) => {
  const isForTexts: string[] = [];

  if (franvaro.FOR_BOKFORDER) {
    isForTexts.push('Bokföringsorder');
  }
  if (franvaro.FOR_UTANORDNING) {
    isForTexts.push('Utanordning');
  }

  return isForTexts.join(', ');
};

const FranvaroListItem: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { classes } = useStyles(props);
  const isSmallScreen = useIsBreakpointOrSmaller('sm');

  const standinNoteIsLongerThen30 = props.franvaro.NOTERING?.length > 30;
  const standInNote = props.franvaro.NOTERING
    ? standinNoteIsLongerThen30
      ? props.franvaro.NOTERING?.substring(0, 30) + '...'
      : props.franvaro.NOTERING
    : '';

  const label = props.isAuto ? 'Delegerad från' : 'Notering';

  const isReservOrReservForLabel = props.isReservFor ? 'Reserv för' : 'Reserv';

  const delegeradFrom = props.franvaro.DelegeradFranPath?.split(',') ?? [];

  const note = props.isAuto
    ? delegeradFrom[props.franvaro?.AUTOSKAPAD_LEVEL ?? 0]
    : standInNote;

  const pointer = props.isAuto
    ? false
    : !props.isReservFor
    ? props.pointer
    : false;

  const itemClass = isSmallScreen ? classes.itemSmall : classes.item;

  const listItemContent = (
    <>
      <EDBox component="span" className={itemClass}>
        <EDTypography
          color={props.isAuto && props.isReservFor ? 'secondary' : undefined}
          bold={props.isReservFor}
        >
          Frånvaroperiod:
        </EDTypography>
        <EDTypography
          color={
            props.isAuto
              ? 'secondary'
              : !props.isReservFor
              ? 'primary'
              : undefined
          }
          bold={!props.isReservFor}
          onClick={props.isAuto ? undefined : props.onEditClick}
          sx={{ cursor: pointer ? 'pointer' : 'default' }}
        >
          {`${props.franvaro.DATUM_FROM} - ${props.franvaro.TomDatumStr ?? ''}`}
        </EDTypography>
      </EDBox>
      <EDBox component="span" className={itemClass}>
        <EDCaptionedValue
          label={isReservOrReservForLabel}
          name={isReservOrReservForLabel}
          noLeftMargin
          topMargin={false}
          fillContainer
        >
          <EDTypography>
            {props.isReservFor
              ? props.franvaro._Associations?.Anvandare?.Name
              : props.franvaro._Associations?.Reserv?.Name}
          </EDTypography>
        </EDCaptionedValue>
      </EDBox>
      <EDBox component="span" className={itemClass}>
        <EDCaptionedValue
          label={'Gäller för'}
          name={'Gäller för'}
          noLeftMargin
          topMargin={false}
          fillContainer
        >
          <EDTypography>{getIsForText(props.franvaro)}</EDTypography>
        </EDCaptionedValue>
      </EDBox>
      <EDBox component="span" className={itemClass}>
        <EDCaptionedValue
          label={label}
          name={label}
          noLeftMargin
          topMargin={false}
          fillContainer
        >
          {standinNoteIsLongerThen30 ? (
            <EDTooltip
              title={
                /* Detta för att få lite större tooltip så att det är lättare att läsa texten än om man bara skickar en text */
                <EDTypography component="span" color={'white'}>
                  {props.franvaro?.NOTERING ?? ''}
                </EDTypography>
              }
            >
              <EDTypography>{note}</EDTypography>
            </EDTooltip>
          ) : (
            <EDTypography>{note}</EDTypography>
          )}
        </EDCaptionedValue>
      </EDBox>
    </>
  );

  return (
    <EDListItem
      className={isSmallScreen ? classes.containerSmall : classes.container}
      testId={`absence${props.isReservFor ? '-for' : ''}-list-item-${
        props.index
      }`}
    >
      {isSmallScreen ? (
        <EDBox component="span">{listItemContent}</EDBox>
      ) : (
        listItemContent
      )}
      <EDBox component="span" className={itemClass}>
        {!props.isAuto && props.onDeleteClick && (
          <EDIconButton
            margin="none"
            icon="delete"
            tooltip="Ta bort frånvaroperiod"
            onClick={props.onDeleteClick}
          />
        )}
      </EDBox>
    </EDListItem>
  );
};

export default FranvaroListItem;
